import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Upload, X as XIcon, Layers } from "lucide-react";

const empty = {
  name: "", slug: "", category: "", subcategory: "", material: "Brass",
  price: 0, mrp: 0, stock: 0, images: "", tags: "", short_description: "", description: "",
  variants: [],
  is_best_seller: false, is_new_arrival: false, is_active: true,
};

const emptyVariant = () => ({ size: "", price: 0, mrp: 0, stock: 0, unit_label: "", dimensions: "", sku: "" });

const parseUrls = (raw) => (raw || "").split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const load = () => api.get("/admin/products").then(({data}) => setItems(data));
  useEffect(() => { load(); api.get("/categories").then(({data})=>setCats(data)); }, []);

  const imageUrls = parseUrls(form.images);
  const mrpNum = parseFloat(form.mrp) || 0;
  const priceNum = parseFloat(form.price) || 0;
  const savings = mrpNum > priceNum ? mrpNum - priceNum : 0;
  const discountPct = mrpNum > 0 && savings > 0 ? Math.round((savings / mrpNum) * 100) : 0;
  const priceGtMrp = priceNum > 0 && mrpNum > 0 && priceNum > mrpNum;

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      const { data } = await api.post("/admin/bulk-upload/images", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const urls = (data.files || []).filter((f) => f.url).map((f) => f.url);
      if (urls.length === 0) { toast.error("Upload failed"); return; }
      const merged = [...imageUrls, ...urls];
      setForm((f) => ({ ...f, images: merged.join("\n") }));
      toast.success(`Uploaded ${urls.length} image${urls.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error("Upload failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = (idx) => {
    const next = imageUrls.filter((_, i) => i !== idx);
    setForm((f) => ({ ...f, images: next.join("\n") }));
  };

  const create = async (e) => {
    e.preventDefault();
    const useVariants = (form.variants || []).length > 0;
    if (!useVariants && priceGtMrp) { toast.error("Selling Price cannot be higher than MRP"); return; }
    // Validate each variant
    if (useVariants) {
      for (const [i, v] of form.variants.entries()) {
        if (!v.size || !String(v.size).trim()) { toast.error(`Variant #${i + 1}: size label is required`); return; }
        const vp = parseFloat(v.price) || 0;
        const vm = parseFloat(v.mrp) || 0;
        if (vp <= 0) { toast.error(`Variant #${i + 1}: price must be > 0`); return; }
        if (vm && vp > vm) { toast.error(`Variant #${i + 1}: selling price cannot exceed MRP`); return; }
      }
    }
    try {
      const cleanedVariants = (form.variants || []).map((v) => ({
        size: String(v.size).trim(),
        price: parseFloat(v.price) || 0,
        mrp: parseFloat(v.mrp) || 0,
        stock: parseInt(v.stock) || 0,
        unit_label: v.unit_label || "",
        dimensions: v.dimensions || "",
        sku: v.sku || "",
      }));
      // When variants exist, default the top-level price/mrp/stock from the min-price variant
      // so legacy fields stay populated (backward compatibility on listing pages).
      let basePrice = priceNum, baseMrp = mrpNum, baseStock = parseInt(form.stock || 0);
      if (useVariants) {
        const minVar = cleanedVariants.reduce((a, b) => (a.price <= b.price ? a : b));
        basePrice = minVar.price;
        baseMrp = minVar.mrp || minVar.price;
        baseStock = cleanedVariants.reduce((s, v) => s + (v.stock || 0), 0);
      }
      const payload = {
        ...form,
        price: basePrice, mrp: baseMrp, stock: baseStock,
        images: imageUrls,
        tags: form.tags.split(",").map(s=>s.trim()).filter(Boolean),
        variants: cleanedVariants,
      };
      await api.post("/admin/products", payload);
      toast.success("Product created");
      setShowForm(false); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
  };

  const addVariant = () => setForm((f) => ({ ...f, variants: [...(f.variants || []), emptyVariant()] }));
  const updateVariant = (idx, patch) => setForm((f) => ({
    ...f,
    variants: (f.variants || []).map((v, i) => (i === idx ? { ...v, ...patch } : v)),
  }));
  const removeVariant = (idx) => setForm((f) => ({
    ...f,
    variants: (f.variants || []).filter((_, i) => i !== idx),
  }));

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div className="space-y-4" data-testid="admin-products">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-4xl">Products</h1>
        <button onClick={()=>setShowForm(!showForm)} className="btn-gold rounded-full px-5 py-2 text-xs uppercase tracking-widest flex items-center gap-2" data-testid="new-product-btn"><Plus className="w-4 h-4" /> New Product</button>
      </div>
      {showForm && (
        <form onSubmit={create} className="border hairline rounded-xl p-5 bg-white grid md:grid-cols-2 gap-3 text-sm" data-testid="new-product-form">
          <input required placeholder="Name" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'')})} className="border hairline rounded px-3 py-2" />
          <input required placeholder="slug" value={form.slug} onChange={(e)=>setForm({...form, slug:e.target.value})} className="border hairline rounded px-3 py-2" />
          <select required value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})} className="border hairline rounded px-3 py-2">
            <option value="">Category…</option>
            {cats.map((c)=><option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <input placeholder="Subcategory" value={form.subcategory} onChange={(e)=>setForm({...form, subcategory:e.target.value})} className="border hairline rounded px-3 py-2" />
          <select value={form.material} onChange={(e)=>setForm({...form, material:e.target.value})} className="border hairline rounded px-3 py-2">
            {["Brass","Bronze","German Silver","Wood"].map((m)=><option key={m}>{m}</option>)}
          </select>
          {(form.variants || []).length === 0 && (
            <input required type="number" placeholder="Stock" value={form.stock} onChange={(e)=>setForm({...form, stock:e.target.value})} className="border hairline rounded px-3 py-2" />
          )}

          {/* Pricing block: hidden when variants are enabled (per-variant price/MRP/stock is the source of truth) */}
          {(form.variants || []).length === 0 && (
          <div className="md:col-span-2 border hairline rounded-lg p-4 bg-muted/30" data-testid="pricing-block">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Pricing (INR)</div>
            <div className="grid md:grid-cols-2 gap-3">
              <label className="block">
                <div className="text-xs text-muted-foreground mb-1">MRP (Maximum Retail Price) *</div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-espresso">₹</span>
                  <input required type="number" min="0" step="0.01" placeholder="e.g. 2999" value={form.mrp} onChange={(e)=>setForm({...form, mrp:e.target.value})} className="w-full border hairline rounded px-3 py-2 pl-7" data-testid="form-mrp" />
                </div>
              </label>
              <label className="block">
                <div className="text-xs text-muted-foreground mb-1">Selling Price *</div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-espresso">₹</span>
                  <input required type="number" min="0" step="0.01" placeholder="e.g. 1999" value={form.price} onChange={(e)=>setForm({...form, price:e.target.value})} className={`w-full border rounded px-3 py-2 pl-7 ${priceGtMrp ? "border-destructive" : "hairline"}`} data-testid="form-price" />
                </div>
              </label>
            </div>
            {priceGtMrp && (
              <div className="mt-2 text-xs text-destructive" data-testid="price-error">Selling Price cannot be higher than MRP.</div>
            )}
            {!priceGtMrp && savings > 0 && (
              <div className="mt-3 flex items-center gap-3 flex-wrap" data-testid="discount-preview">
                <span className="text-lg font-serif gold-text">₹{priceNum.toLocaleString("en-IN")}</span>
                <span className="text-sm text-muted-foreground line-through">₹{mrpNum.toLocaleString("en-IN")}</span>
                <span className="text-[10px] uppercase tracking-widest bg-espresso text-ivory px-2 py-0.5 rounded-full">-{discountPct}% OFF</span>
                <span className="text-xs text-emerald-700 font-medium">You Save ₹{savings.toLocaleString("en-IN")}</span>
              </div>
            )}
          </div>
          )}

          {/* Variants block: multi-size / multi-price / per-variant stock, unit & dimensions */}
          <div className="md:col-span-2 border hairline rounded-lg p-4 bg-muted/30" data-testid="variants-block">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 gold-text" strokeWidth={1.5} />
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Multiple Variants (Sizes)</div>
              </div>
              <button type="button" onClick={addVariant} className="btn-outline-gold rounded-full px-3 py-1.5 text-[11px] uppercase tracking-widest inline-flex items-center gap-1" data-testid="add-variant-btn">
                <Plus className="w-3 h-3" strokeWidth={1.5} /> Add Variant
              </button>
            </div>
            {(form.variants || []).length === 0 && (
              <div className="text-[11px] text-muted-foreground">Add variants for products sold in multiple sizes (e.g. 8&quot; / 10&quot; / 12&quot; Diyas). Each variant has its own price, MRP, stock, unit and dimensions. Leave empty to use the single-price fields above.</div>
            )}
            {(form.variants || []).map((v, i) => {
              const vp = parseFloat(v.price) || 0;
              const vm = parseFloat(v.mrp) || 0;
              const vErr = vm > 0 && vp > vm;
              return (
                <div key={i} className="mt-3 border hairline rounded-lg p-3 bg-white" data-testid={`variant-row-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Variant #{i + 1}</div>
                    <button type="button" onClick={() => removeVariant(i)} className="text-xs text-destructive inline-flex items-center gap-1" data-testid={`remove-variant-${i}`}>
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                    <label className="block">
                      <div className="text-muted-foreground mb-1">Size Label *</div>
                      <input required value={v.size} onChange={(e) => updateVariant(i, { size: e.target.value })} placeholder='e.g. 10"' className="w-full border hairline rounded px-2 py-1.5" data-testid={`variant-size-${i}`} />
                    </label>
                    <label className="block">
                      <div className="text-muted-foreground mb-1">Price ₹ *</div>
                      <input required type="number" min="0" step="0.01" value={v.price} onChange={(e) => updateVariant(i, { price: e.target.value })} className={`w-full border rounded px-2 py-1.5 ${vErr ? "border-destructive" : "hairline"}`} data-testid={`variant-price-${i}`} />
                    </label>
                    <label className="block">
                      <div className="text-muted-foreground mb-1">MRP ₹</div>
                      <input type="number" min="0" step="0.01" value={v.mrp} onChange={(e) => updateVariant(i, { mrp: e.target.value })} className="w-full border hairline rounded px-2 py-1.5" data-testid={`variant-mrp-${i}`} />
                    </label>
                    <label className="block">
                      <div className="text-muted-foreground mb-1">Stock</div>
                      <input type="number" min="0" value={v.stock} onChange={(e) => updateVariant(i, { stock: e.target.value })} className="w-full border hairline rounded px-2 py-1.5" data-testid={`variant-stock-${i}`} />
                    </label>
                    <label className="block">
                      <div className="text-muted-foreground mb-1">Unit</div>
                      <input value={v.unit_label} onChange={(e) => updateVariant(i, { unit_label: e.target.value })} placeholder="per Pair / per Set" className="w-full border hairline rounded px-2 py-1.5" data-testid={`variant-unit-${i}`} />
                    </label>
                    <label className="block">
                      <div className="text-muted-foreground mb-1">Dimensions</div>
                      <input value={v.dimensions} onChange={(e) => updateVariant(i, { dimensions: e.target.value })} placeholder='e.g. 10" H · 300g' className="w-full border hairline rounded px-2 py-1.5" data-testid={`variant-dim-${i}`} />
                    </label>
                  </div>
                  {vErr && <div className="mt-1 text-[11px] text-destructive">Price cannot exceed MRP for this variant.</div>}
                </div>
              );
            })}
          </div>

          {/* Image uploader — permanent object-storage URLs, keeps existing URL textarea */}
          <div className="md:col-span-2 border hairline rounded-lg p-4 bg-muted/30" data-testid="image-block">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Product Images</div>
              <div className="flex items-center gap-2">
                <input ref={fileInputRef} type="file" multiple accept="image/*" className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="upload-image-input" />
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="btn-gold rounded-full px-4 py-1.5 text-[11px] uppercase tracking-widest inline-flex items-center gap-1.5" data-testid="upload-image-btn">
                  <Upload className="w-3.5 h-3.5" strokeWidth={1.5} /> {uploading ? "Uploading…" : "Upload Image"}
                </button>
              </div>
            </div>
            {imageUrls.length > 0 && (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-3" data-testid="image-preview-grid">
                {imageUrls.map((u, i) => (
                  <div key={u + i} className="relative group aspect-square rounded border hairline overflow-hidden bg-white">
                    <img src={u} alt="preview" className="w-full h-full object-cover" />
                    {i === 0 && <span className="absolute top-1 left-1 text-[9px] bg-espresso text-ivory px-1.5 py-0.5 rounded uppercase tracking-widest">Main</span>}
                    <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 hover:bg-black text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition" data-testid={`remove-image-${i}`} aria-label="Remove image">
                      <XIcon className="w-3 h-3" strokeWidth={2} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <textarea placeholder="Or paste image URLs (comma or newline separated)" value={form.images} onChange={(e)=>setForm({...form, images:e.target.value})} className="w-full border hairline rounded px-3 py-2 text-xs font-mono" rows={2} data-testid="image-urls-textarea" />
            <div className="mt-1 text-[10px] text-muted-foreground">Tip: the first image becomes the main product image. On mobile, "Upload Image" opens your Gallery / Files / Camera picker so you can pick multiple photos at once.</div>
          </div>
          <input placeholder="Tags (comma-separated)" value={form.tags} onChange={(e)=>setForm({...form, tags:e.target.value})} className="md:col-span-2 border hairline rounded px-3 py-2" />
          <textarea placeholder="Short description" value={form.short_description} onChange={(e)=>setForm({...form, short_description:e.target.value})} className="md:col-span-2 border hairline rounded px-3 py-2" rows={2} />
          <textarea placeholder="Full description" value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} className="md:col-span-2 border hairline rounded px-3 py-2" rows={4} />
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_best_seller} onChange={(e)=>setForm({...form, is_best_seller:e.target.checked})} /> Best seller</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_new_arrival} onChange={(e)=>setForm({...form, is_new_arrival:e.target.checked})} /> New arrival</label>
          <button className="md:col-span-2 btn-gold rounded-full py-2 text-xs uppercase tracking-widest">Create Product</button>
        </form>
      )}
      <div className="border hairline rounded-xl bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr><th className="p-3"></th><th>Name</th><th>Selling Price ₹</th><th>MRP ₹</th><th>Stock</th><th>Images</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <AdminProductRow key={p.product_id} product={p} onReload={load} onDelete={del} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// Inline-edit row: change Selling Price, MRP, Stock, and add/remove product photos
// with the same Camera / Gallery / Files picker used in the create form.
function AdminProductRow({ product, onReload, onDelete }) {
  const [price, setPrice] = useState(product.price ?? 0);
  const [mrp, setMrp] = useState(product.mrp ?? 0);
  const [stock, setStock] = useState(product.stock ?? 0);
  const [images, setImages] = useState(product.images || []);
  const [variants, setVariants] = useState(product.variants || []);
  const [showVariants, setShowVariants] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const rowInputRef = useRef(null);
  const priceNum = parseFloat(price) || 0;
  const mrpNum = parseFloat(mrp) || 0;
  const priceGtMrp = priceNum > 0 && mrpNum > 0 && priceNum > mrpNum;
  const hasVariants = (variants || []).length > 0;
  const variantsDirty = JSON.stringify(variants) !== JSON.stringify(product.variants || []);
  const dirty = priceNum !== product.price || mrpNum !== product.mrp || parseInt(stock) !== product.stock || JSON.stringify(images) !== JSON.stringify(product.images || []) || variantsDirty;

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      const { data } = await api.post("/admin/bulk-upload/images", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const urls = (data.files || []).filter((f) => f.url).map((f) => f.url);
      if (urls.length) setImages((prev) => [...prev, ...urls]);
      toast.success(`Uploaded ${urls.length} image${urls.length === 1 ? "" : "s"}`);
    } catch (e) { toast.error("Upload failed: " + (e.response?.data?.detail || e.message)); }
    finally { setUploading(false); if (rowInputRef.current) rowInputRef.current.value = ""; }
  };

  const save = async () => {
    if (!hasVariants && priceGtMrp) { toast.error("Selling Price cannot be higher than MRP"); return; }
    if (hasVariants) {
      for (const [i, v] of variants.entries()) {
        if (!v.size || !String(v.size).trim()) { toast.error(`Variant #${i + 1}: size label is required`); return; }
        const vp = parseFloat(v.price) || 0;
        const vm = parseFloat(v.mrp) || 0;
        if (vp <= 0) { toast.error(`Variant #${i + 1}: price must be > 0`); return; }
        if (vm && vp > vm) { toast.error(`Variant #${i + 1}: selling price cannot exceed MRP`); return; }
      }
    }
    setSaving(true);
    try {
      const payload = { images };
      if (hasVariants) {
        const cleaned = variants.map((v) => ({
          variant_id: v.variant_id || undefined,
          size: String(v.size).trim(),
          price: parseFloat(v.price) || 0,
          mrp: parseFloat(v.mrp) || 0,
          stock: parseInt(v.stock) || 0,
          unit_label: v.unit_label || "",
          dimensions: v.dimensions || "",
          sku: v.sku || "",
        }));
        payload.variants = cleaned;
        // keep top-level price/mrp/stock in sync for listing/SEO
        const minVar = cleaned.reduce((a, b) => (a.price <= b.price ? a : b));
        payload.price = minVar.price;
        payload.mrp = minVar.mrp || minVar.price;
        payload.stock = cleaned.reduce((s, v) => s + (v.stock || 0), 0);
      } else {
        payload.price = priceNum;
        payload.mrp = mrpNum;
        payload.stock = parseInt(stock) || 0;
        payload.variants = [];
      }
      await api.patch(`/admin/products/${product.product_id}`, payload);
      toast.success("Product updated");
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const addVariant = () => setVariants((prev) => [...(prev || []), { size: "", price: 0, mrp: 0, stock: 0, unit_label: "", dimensions: "", sku: "" }]);
  const patchVariant = (idx, p) => setVariants((prev) => prev.map((v, i) => (i === idx ? { ...v, ...p } : v)));
  const dropVariant = (idx) => setVariants((prev) => prev.filter((_, i) => i !== idx));

  return (
    <>
    <tr className="border-t hairline align-top" data-testid={`row-${product.product_id}`}>
      <td className="p-3"><img src={images[0]} className="w-14 h-14 rounded object-cover" alt="" /></td>
      <td className="p-3">
        <div className="font-medium">{product.name}</div>
        <div className="text-xs text-muted-foreground">{product.sku} · {product.category}</div>
        {hasVariants && (
          <button type="button" onClick={() => setShowVariants((v) => !v)} className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest gold-text hover:underline" data-testid={`row-toggle-variants-${product.product_id}`}>
            <Layers className="w-3 h-3" strokeWidth={1.5} /> {variants.length} variant{variants.length === 1 ? "" : "s"} · {showVariants ? "hide" : "manage"}
          </button>
        )}
        <div className="mt-2 flex flex-wrap gap-1 max-w-[220px]">
          {images.map((u, i) => (
            <div key={u + i} className="relative w-9 h-9 rounded overflow-hidden border hairline group">
              <img src={u} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => setImages(images.filter((_, j) => j !== i))} className="absolute inset-0 bg-black/60 text-white text-[10px] opacity-0 group-hover:opacity-100 flex items-center justify-center" data-testid={`row-remove-${product.product_id}-${i}`}>✕</button>
            </div>
          ))}
          <input ref={rowInputRef} type="file" multiple accept="image/*" className="hidden" onChange={(e) => uploadFiles(e.target.files)} data-testid={`row-upload-input-${product.product_id}`} />
          <button type="button" onClick={() => rowInputRef.current?.click()} disabled={uploading} className="w-9 h-9 rounded border hairline flex items-center justify-center hover:border-[#B8860B] gold-text" data-testid={`row-upload-${product.product_id}`} title="Add photos (camera / gallery / files)">
            {uploading ? "…" : <Upload className="w-4 h-4" strokeWidth={1.5} />}
          </button>
        </div>
      </td>
      <td className="p-3">
        {hasVariants ? <span className="text-xs text-muted-foreground">per variant</span> : (
          <input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className={`w-24 border rounded px-2 py-1 ${priceGtMrp ? "border-destructive" : "hairline"}`} data-testid={`row-price-${product.product_id}`} />
        )}
      </td>
      <td className="p-3">
        {hasVariants ? <span className="text-xs text-muted-foreground">per variant</span> : (
          <input type="number" min="0" step="0.01" value={mrp} onChange={(e) => setMrp(e.target.value)} className="w-24 border hairline rounded px-2 py-1" data-testid={`row-mrp-${product.product_id}`} />
        )}
      </td>
      <td className="p-3">
        {hasVariants ? <span className="text-xs text-muted-foreground">{variants.reduce((s,v)=>s+(parseInt(v.stock)||0),0)} total</span> : (
          <input type="number" min="0" value={stock} onChange={(e) => setStock(e.target.value)} className="w-20 border hairline rounded px-2 py-1" data-testid={`row-stock-${product.product_id}`} />
        )}
      </td>
      <td className="p-3 text-xs text-muted-foreground">{images.length} photo{images.length === 1 ? "" : "s"}</td>
      <td className="p-3 text-right whitespace-nowrap">
        <button onClick={save} disabled={!dirty || saving || (!hasVariants && priceGtMrp)} className={`text-xs px-3 py-1 rounded-full mr-2 ${dirty && !(!hasVariants && priceGtMrp) ? "btn-gold" : "border hairline text-muted-foreground opacity-60"}`} data-testid={`row-save-${product.product_id}`}>{saving ? "Saving…" : "Save"}</button>
        <button onClick={() => onDelete(product.product_id)} className="text-xs text-destructive inline-flex items-center gap-1" data-testid={`del-${product.product_id}`}><Trash2 className="w-3 h-3" />Delete</button>
      </td>
    </tr>
    {(showVariants || (!hasVariants && variantsDirty)) && (
      <tr className="bg-muted/20 border-t hairline">
        <td colSpan={7} className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1"><Layers className="w-3 h-3" strokeWidth={1.5} /> Variants</div>
            <button type="button" onClick={addVariant} className="btn-outline-gold rounded-full px-3 py-1 text-[11px] uppercase tracking-widest inline-flex items-center gap-1" data-testid={`row-add-variant-${product.product_id}`}>
              <Plus className="w-3 h-3" strokeWidth={1.5} /> Add Variant
            </button>
          </div>
          {(variants || []).length === 0 && <div className="text-[11px] text-muted-foreground">No variants. Add one to enable per-size price / stock / dimensions.</div>}
          <div className="space-y-2">
            {(variants || []).map((v, i) => {
              const vp = parseFloat(v.price) || 0;
              const vm = parseFloat(v.mrp) || 0;
              const vErr = vm > 0 && vp > vm;
              return (
                <div key={v.variant_id || i} className="grid grid-cols-2 md:grid-cols-7 gap-2 text-xs items-end bg-white border hairline rounded p-2" data-testid={`row-variant-${product.product_id}-${i}`}>
                  <label className="block"><div className="text-muted-foreground mb-0.5">Size *</div><input value={v.size} onChange={(e) => patchVariant(i, { size: e.target.value })} placeholder='10"' className="w-full border hairline rounded px-2 py-1" data-testid={`row-variant-size-${product.product_id}-${i}`} /></label>
                  <label className="block"><div className="text-muted-foreground mb-0.5">Price ₹</div><input type="number" min="0" step="0.01" value={v.price} onChange={(e) => patchVariant(i, { price: e.target.value })} className={`w-full border rounded px-2 py-1 ${vErr ? "border-destructive" : "hairline"}`} data-testid={`row-variant-price-${product.product_id}-${i}`} /></label>
                  <label className="block"><div className="text-muted-foreground mb-0.5">MRP ₹</div><input type="number" min="0" step="0.01" value={v.mrp} onChange={(e) => patchVariant(i, { mrp: e.target.value })} className="w-full border hairline rounded px-2 py-1" data-testid={`row-variant-mrp-${product.product_id}-${i}`} /></label>
                  <label className="block"><div className="text-muted-foreground mb-0.5">Stock</div><input type="number" min="0" value={v.stock} onChange={(e) => patchVariant(i, { stock: e.target.value })} className="w-full border hairline rounded px-2 py-1" data-testid={`row-variant-stock-${product.product_id}-${i}`} /></label>
                  <label className="block"><div className="text-muted-foreground mb-0.5">Unit</div><input value={v.unit_label} onChange={(e) => patchVariant(i, { unit_label: e.target.value })} placeholder="per Pair" className="w-full border hairline rounded px-2 py-1" /></label>
                  <label className="block col-span-2 md:col-span-1"><div className="text-muted-foreground mb-0.5">Dimensions</div><input value={v.dimensions} onChange={(e) => patchVariant(i, { dimensions: e.target.value })} placeholder='10" H · 300g' className="w-full border hairline rounded px-2 py-1" /></label>
                  <div className="flex md:justify-end"><button type="button" onClick={() => dropVariant(i)} className="text-xs text-destructive inline-flex items-center gap-1" data-testid={`row-drop-variant-${product.product_id}-${i}`}><Trash2 className="w-3 h-3" />Remove</button></div>
                </div>
              );
            })}
          </div>
        </td>
      </tr>
    )}
    </>
  );
}
